import React, { useState, memo, useCallback } from "react";
import { ToDo } from "@/types/trip";
import {
  addTodo as addTodoLib,
  toggleTodoComplete as toggleTodoCompleteLib,
  deleteTodo as deleteTodoLib,
} from "@/lib/lodge";
import { XCircleIcon, ClipboardIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { useQueryClient } from "@tanstack/react-query";
import useCurrencyStore from "@/stores/ui/currencyStore";
import logger from "@/utils/logger";

interface TodoInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onAdd: () => void;
  placeholderTextSize?: string;
}

const TodoInput = memo(
  ({
    value,
    onChange,
    onKeyDown,
    onAdd,
    placeholderTextSize,
  }: TodoInputProps) => {
    return (
      <div className="relative group">
        <div className="relative flex items-center rounded-lg p-1 2xl:p-2">
          <Input
            type="text"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="What needs to be done?"
            className={`flex-1 h-8 2xl:h-9 pl-3 pr-10 text-sm 2xl:text-base text-neutral-800 font-light placeholder:font-light bg-transparent placeholder-neutral-400 border-none focus:outline-none focus:ring-0 ${
              placeholderTextSize
                ? `placeholder:text-${placeholderTextSize} 2xl:placeholder:text-sm`
                : "placeholder:text-xs 2xl:placeholder:text-sm"
            }`}
          />
          <button
            onClick={onAdd}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 2xl:w-8 2xl:h-8 flex items-center justify-center text-white bg-indigo-700 rounded-full transition-all duration-300 shadow-sm"
            title="Add task"
          >
            <i className="fa-solid fa-plus text-xs 2xl:text-sm"></i>
          </button>
        </div>
      </div>
    );
  }
);

TodoInput.displayName = "TodoInput";

interface TodoItemProps {
  todo: ToDo;
  onToggle: (todoId: number) => void;
  onDelete: (todo: ToDo) => void;
}

const formatTodoTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  // Format time
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  };
  const time = date.toLocaleTimeString(undefined, timeOptions);

  // Check if today
  if (date.toDateString() === now.toDateString()) {
    return `Today, ${time}`;
  }

  // Check if yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${time}`;
  }

  // Otherwise show date
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };
  const dateStr = date.toLocaleDateString(undefined, dateOptions);
  return `${dateStr}, ${time}`;
};

const TodoItem = memo(({ todo, onToggle, onDelete }: TodoItemProps) => (
  <div
    className={`group relative overflow-hidden transition-all duration-300 ease-in-out ${
      todo.completed ? "opacity-70" : ""
    }`}
  >
    <div
      className="relative flex items-center py-2 2xl:py-2 pl-3 pr-2 transition-all duration-300 cursor-pointer"
      onClick={() => onToggle(todo.id)}
    >
      <div
        className={`shrink-0 relative w-3 h-3 2xl:w-4 2xl:h-4 rounded-full flex items-center justify-center transition-all duration-300 ease-in-out ${
          todo.completed
            ? "bg-gradient-to-r from-indigo-600 to-purple-600"
            : "ring-1 ring-indigo-400 hover:ring-indigo-600"
        }`}
      ></div>
      <div className="flex-grow ml-3 flex flex-col">
        <span
          className={`text-xs 2xl:text-sm font-light ${
            todo.completed ? "text-neutral-500" : "text-neutral-800"
          }`}
        >
          {todo.text}
        </span>
        <span className="text-[10px] 2xl:text-xs text-neutral-400 mt-0.5">
          {formatTodoTime(todo.created_at)}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(todo);
        }}
        className="p-1 rounded-full text-neutral-400 hover:text-rose-500 hover:bg-rose-50 transition-all duration-300 opacity-0 group-hover:opacity-100"
        title="Delete task"
      >
        <XCircleIcon className="w-6 h-6 2xl:w-7 2xl:h-7" />
      </button>
    </div>
  </div>
));

TodoItem.displayName = "TodoItem";

interface ToDoListProps {
  todos: ToDo[] | null;
  tripId: string;
  placeholderTextSize?: string;
}

const ToDoList: React.FC<ToDoListProps> = ({
  todos,
  tripId,
  placeholderTextSize = "xs",
}) => {
  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const [inputValue, setInputValue] = useState("");
  const { toast } = useToast();
  const setTripTodos = useTripStore((state) => state.setTripTodos);
  const queryClient = useQueryClient();
  const currencyCode = selectedCurrency.code;

  // Optimistic update functions
  const addTodo = useCallback(
    (tripId: string, todoText: string | ToDo) => {
      const newTodo: ToDo =
        typeof todoText === "string"
          ? {
              id: Math.floor(Math.random() * 1000000),
              text: todoText,
              completed: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          : todoText;

      if (!todos || todos.length === 0) {
        setTripTodos([newTodo]);
      } else {
        setTripTodos([...todos, newTodo]);
      }
    },
    [todos, setTripTodos]
  );

  const updateTodoId = useCallback(
    (tripId: string, tempId: number, realId: number) => {
      const currentTodos = useTripStore.getState().tripTodos;
      logger.log("currentTodos", currentTodos);
      const updatedTodos = currentTodos.map((todo) =>
        todo.id === tempId ? { ...todo, id: realId } : todo
      );
      logger.log("updatedTodos", updatedTodos);
      setTripTodos(updatedTodos);
    },
    [setTripTodos]
  );

  const deleteTodo = useCallback(
    (tripId: string, todoId: number) => {
      if (!todos) return;
      const updatedTodos = todos.filter((todo) => todo.id !== todoId);
      setTripTodos(updatedTodos);
    },
    [todos, setTripTodos]
  );

  const toggleTodoComplete = useCallback(
    (tripId: string, todoId: number) => {
      if (!todos) return;
      const updatedTodos = todos.map((todo) =>
        todo.id === todoId
          ? {
              ...todo,
              completed: !todo.completed,
              updated_at: new Date().toISOString(),
            }
          : todo
      );
      setTripTodos(updatedTodos);
    },
    [todos, setTripTodos]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleAddTodo = useCallback(
    async (e?: React.KeyboardEvent<HTMLInputElement>) => {
      if (e && e.key !== "Enter") return;

      if (inputValue.trim() !== "") {
        const tempId = Math.floor(Math.random() * 1000000);
        const newTodo = {
          id: tempId,
          text: inputValue.trim(),
          completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setInputValue("");

        // add to do to list
        addTodo(tripId, newTodo);

        try {
          const response = await addTodoLib(tripId, inputValue.trim());
          logger.log("addTodoLib response", response);
          // Update the todo with the real ID from the server
          updateTodoId(tripId, tempId, response.id);
          // Invalidate trips query to refresh todo data
          queryClient.invalidateQueries({
            queryKey: ["trips", currencyCode],
          });
        } catch (error) {
          logger.log("error", error);
          // Remove the todo if the API call fails
          deleteTodo(tripId, tempId);
          toast({
            title: "Error adding todo",
            description: "There was an error adding the todo.",
            duration: 5000,
            variant: "destructive",
          });
        }
      }
    },
    [
      inputValue,
      tripId,
      toast,
      addTodo,
      deleteTodo,
      updateTodoId,
      queryClient,
      currencyCode,
    ]
  );

  const handleToggleTodo = useCallback(
    async (todoId: number) => {
      toggleTodoComplete(tripId, todoId);

      try {
        await toggleTodoCompleteLib(tripId, todoId);
        // Invalidate trips query to refresh todo data
        queryClient.invalidateQueries({
          queryKey: ["trips", currencyCode],
        });
      } catch (error) {
        console.error("Failed to toggle todo:", error);
        // Revert the toggle if the API call fails
        toggleTodoComplete(tripId, todoId);
        toast({
          title: "Error updating todo",
          description: "There was an error updating the todo status.",
          duration: 5000,
          variant: "destructive",
        });
      }
    },
    [tripId, toast, toggleTodoComplete, queryClient, currencyCode]
  );

  const handleDeleteTodo = useCallback(
    async (todo: ToDo) => {
      deleteTodo(tripId, todo.id);

      try {
        await deleteTodoLib(tripId, todo.id);
        // Invalidate trips query to refresh todo data
        queryClient.invalidateQueries({
          queryKey: ["trips", currencyCode],
        });
      } catch (error) {
        // Add the todo back if the API call fails
        addTodo(tripId, todo);
        console.error("Failed to delete todo:", error);
        toast({
          title: "Error deleting todo",
          description: "There was an error deleting the todo.",
          duration: 5000,
          variant: "destructive",
        });
      }
    },
    [tripId, toast, deleteTodo, addTodo, queryClient, currencyCode]
  );

  return (
    <div className="flex-1 flex flex-col w-full h-full rounded-xl overflow-hidden border border-neutral-200 pb-3">
      {/* Header */}
      <div className="bg-white rounded-b-md border-b border-neutral-200 shadow-sm">
        <TodoInput
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleAddTodo}
          onAdd={() => handleAddTodo()}
          placeholderTextSize={placeholderTextSize}
        />
      </div>

      {/* List Section */}
      {!todos || todos.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-neutral-400 rounded-lg p-6 2xl:p-8 space-y-2 2xl:space-y-3">
          <div className="w-16 h-16 2xl:w-20 2xl:h-20 rounded-full bg-indigo-50 flex items-center justify-center">
            <ClipboardIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-indigo-400" />
          </div>
          <div className="text-center">
            <p className="text-sm 2xl:text-base font-medium text-indigo-900">
              No tasks yet
            </p>
            <p className="text-xs 2xl:text-sm text-neutral-500 font-light mt-1">
              Add your first task to get started
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {todos &&
            todos.map((todo: ToDo) => (
              <TodoItem
                key={todo.id}
                todo={todo}
                onToggle={handleToggleTodo}
                onDelete={handleDeleteTodo}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default ToDoList;
